'use client';

import { useEffect, useState } from 'react';
import useGetAccreditationEligibility from 'hooks/useGetAccreditationEligibility';
import { PH_BOOTSTRAP_COOKIE, parseBootstrapCookie } from 'utils/bootstrapHelpers';
import { COOKIE_KEYS } from 'utils/constants/localStorage';

const readCookieValue = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length !== 2) return null;
    const cookieValue = parts.pop()?.split(';').shift();
    return cookieValue ? decodeURIComponent(cookieValue) : null;
  } catch {
    return null;
  }
};

const isAccreditedFromCookies = (): boolean => {
  const bootstrap = parseBootstrapCookie(readCookieValue(PH_BOOTSTRAP_COOKIE) ?? undefined);
  if (bootstrap?.predictedExperience === 'accredited') return true;
  return readCookieValue(COOKIE_KEYS.PREDICTED_EXPERIENCE) === 'accredited';
};

interface Options {
  primaryUserId?: string;
  enabled?: boolean;
}

const usePotentiallyAccredited = ({ primaryUserId, enabled = true }: Options) => {
  const {
    isAccredited: isVerifiedAccredited,
    eligibilityStatus,
    isLoading,
  } = useGetAccreditationEligibility({
    primaryUserId,
    enabled,
  });

  const [predictedAccredited, setPredictedAccredited] = useState<boolean>(false);

  useEffect(() => {
    setPredictedAccredited(isAccreditedFromCookies());
  }, []);

  const isExplicitlyNotEligible = eligibilityStatus === 'NOT_ELIGIBLE';
  const isPotentiallyAccredited = isExplicitlyNotEligible
    ? false
    : isVerifiedAccredited || predictedAccredited;

  return {
    isPotentiallyAccredited,
    isVerifiedAccredited,
    isPredictedAccredited: predictedAccredited,
    isLoading,
  };
};

export default usePotentiallyAccredited;
